<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      $blockClass(blockClass, 'prices'),
      {
        'product-prices--reverse': reverse,
        [$blockClass(blockClass, 'prices--reverse')]: reverse,
        [classNormal]: available,
        [classSoldOut]: available === false,
      },
    ]"
  >
    <span
      v-if="isFreeGift"
      class="product-prices__free-gift"
      v-text="$string('cart.free_gift.worth')"
    />

    <span
      v-if="formattedCompareAtPrice && !reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />

    <span
      class="product-prices__price"
      :class="[
        $blockClass(blockClass, 'price'),
        classPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
      v-text="formattedPrice"
    />

    <span
      v-if="formattedCompareAtPrice && reverse"
      class="product-prices__compare-at-price"
      :class="$blockClass(blockClass, 'compare-at-price')"
      v-text="formattedCompareAtPrice"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classNormal] - Text class for normal prices.
 * @param {String} [classPrice] - Text class for price only (i.e. not compare
 * at/original price).
 * @param {String} [classSoldOut] - Text class for sold out products.
 * @param {Number|Boolean} compareAtPrice - Compare at price if it exists.
 * @param {String} [element] - Custom element override.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [reverse] - Reverse order of prices.
 * @param {Boolean} [varies] - Price varies.
 * @param {Boolean} isFreeGift - If product is a gift.
 *
 */
export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    classNormal: {
      type: String,
      default: '',
    },
    classPrice: {
      type: String,
      default: '',
    },
    classSoldOut: {
      type: String,
      default: '',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
    isFreeGift: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return this.$string('accessibility.loading')
      }

      if (this.price <= 0) {
        return this.$string('product.prices.free')
      }

      return this.varies
        ? this.$string('product.prices.from', { replace: { price: this.$formatMoney(this.price) } })
        : this.$formatMoney(this.price)
    },
  },
}
</script>
